/**
 * Keeps users.slotCheckSearchEnabled fresh in redux (side-menu item + header
 * globe) from ms-casino GetSlotCheckSearchAccess: on sign-in / app load, when
 * the app comes back to the foreground, and when the side menu opens
 * (throttled). Errors (e.g. ms-casino not deployed yet) keep the current value.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { ReduxCommandCreator } from 'appRedux/actions';
import { CasinoService } from 'services/casino';
import { accessValueFromResponse, shouldRefreshAccess } from 'lib/slotCheckSearchAccessRefresh';

export function useSlotCheckSearchAccessRefresh(signedIn: boolean): (force?: boolean) => void {
  const dispatch = useDispatch();
  const commands = useMemo(() => ReduxCommandCreator(dispatch), [dispatch]);
  const casinoSvc = useMemo(() => new CasinoService(), []);
  const lastAtRef = useRef<number | null>(null);
  const inFlightRef = useRef(false);

  const refresh = useCallback(
    (force?: boolean) => {
      if (!signedIn || inFlightRef.current) return;
      const now = Date.now();
      if (!shouldRefreshAccess(lastAtRef.current, now, !!force)) return;
      lastAtRef.current = now;
      inFlightRef.current = true;
      casinoSvc.GetSlotCheckSearchAccess().subscribe({
        next: (resp) => {
          inFlightRef.current = false;
          const value = accessValueFromResponse(resp);
          if (value !== undefined) commands.SetSlotCheckSearchEnabled(value);
        },
        error: () => {
          inFlightRef.current = false;
        },
      });
    },
    [signedIn, casinoSvc, commands]
  );

  useEffect(() => {
    if (!signedIn) {
      lastAtRef.current = null;
      return undefined;
    }
    refresh(true);
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [signedIn, refresh]);

  return refresh;
}
